import { useCallback } from 'react'
import {
  APIError,
  type DecoratedCard,
  type CardEntityId,
  type CardGetInput,
  type CardGetSuccess,
  isNotEmpty,
} from '@terros/common'
import { useQuery } from '@tanstack/react-query'
import { type GetHookResponse } from '../hooks'
import { useServerRequest } from '../api'
import { cardKey } from './keys'

export function useCard(entityId: CardEntityId | undefined): GetHookResponse<DecoratedCard | undefined> {
  const serverRequest = useServerRequest<CardGetInput, CardGetSuccess>('/card/get')

  const queryFn = useCallback(async (): Promise<DecoratedCard | null> => {
    try {
      const result = await serverRequest({ entityId: entityId ?? 'C.EMPTY_CARD' })
      return result.card
    } catch (error) {
      if (error instanceof APIError && error.error.error === 'NotFound') return null
      throw error
    }
  }, [entityId, serverRequest])

  const query = useQuery({
    queryKey: cardKey(entityId),
    queryFn,
    enabled: isNotEmpty(entityId),
    retry: false,
  })

  return {
    ...query,
    data: query.data ?? undefined,
    error: query.error?.message,
    lastUpdatedAt: query.isSuccess ? query.dataUpdatedAt : undefined,
  }
}
